import { createFileRoute, Link, Navigate } from "@tanstack/react-router";
import { useTranslation } from "react-i18next";
import { useAuth } from "@/hooks/use-auth";
import { ThemeToggle } from "@/components/ThemeToggle";
import { BrandLogo } from "@/components/BrandLogo";
import { PricingPlanGrid } from "@/components/PricingPlanGrid";

export const Route = createFileRoute("/pricing")({
  head: () => ({
    meta: [
      { title: "Pricing — Lumvio client portal & Payment Protection" },
      { name: "description", content: "Choose the Lumvio plan that fits your freelance workflow. Free, Starter $9.99, Pro $19.99, Unlimited $34.99 — 0% commission on earnings, Payment Protection included." },
      { name: "keywords", content: "Lumvio pricing, freelance client portal pricing, freelance payment protection plans, 0% commission freelance platform, freelance invoicing subscription" },
      { property: "og:title", content: "Pricing — Lumvio client portal & Payment Protection" },
      { property: "og:description", content: "Free, Starter, Pro, Unlimited. 0% commission on earnings, Payment Protection included. Cancel anytime." },
      { property: "og:url", content: "https://lumvio.co/pricing" },
      { property: "og:type", content: "website" },
      { property: "og:image", content: "https://lumvio.co/__l5e/assets-v1/bbd15e4b-2805-4707-8582-06a259cb86cc/lumvio-og-v3.png" },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: "Lumvio pricing — 0% commission plans" },
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:image", content: "https://lumvio.co/__l5e/assets-v1/bbd15e4b-2805-4707-8582-06a259cb86cc/lumvio-og-v3.png" },
    ],
    links: [
      { rel: "canonical", href: "https://lumvio.co/pricing" },
      { rel: "alternate", hrefLang: "en", href: "https://lumvio.co/pricing" },
      { rel: "alternate", hrefLang: "ar", href: "https://lumvio.co/pricing" },
      { rel: "alternate", hrefLang: "x-default", href: "https://lumvio.co/pricing" },
    ],
  }),
  component: PricingPage,
});

function PricingPage() {
  const { profile } = useAuth();
  const { t } = useTranslation();

  if (profile && profile.role !== "freelancer") {
    return <Navigate to="/dashboard" />;
  }

  const currentTier = profile?.subscription_tier ?? "free";
  const kycVerified = profile?.kyc_status === "verified";

  return (
    <div className="relative min-h-screen w-full overflow-x-hidden bg-background text-foreground">
      {/* Full-bleed ambient wash so left/right edges match the middle */}
      <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
        <div className="absolute -top-24 left-1/2 h-[520px] w-[880px] -translate-x-1/2 rounded-full bg-primary/10 blur-[140px]" />
      </div>

      {/* Header: clickable logo back to home + theme toggle */}
      <header className="relative z-10 mx-auto flex max-w-6xl items-center justify-between px-6 py-5">
        <Link to="/" aria-label="Back to home" className="transition-opacity hover:opacity-80">
          <BrandLogo size="md" />
        </Link>
        <ThemeToggle />
      </header>

      <div className="relative mx-auto max-w-6xl space-y-16 px-6 pb-16">
        <PricingPlanGrid
          currentTier={currentTier}
          kycVerified={kycVerified}
          showBadge
          showTitle
          showFootnote
        />

        {/* FAQ teaser */}
        <div className="text-center opacity-0 [animation:fade-in_0.6s_ease-out_0.8s_forwards]">
          <p className="text-sm text-muted-foreground">
            {t("pricingPage.questions")}{" "}
            <a href="mailto:contact@lumvio.co" className="text-primary hover:underline underline-offset-4">
              {t("pricingPage.contact")}
            </a>
          </p>
        </div>
      </div>
    </div>
  );
}
